import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import LocationHistoryMap from '@/components/bikes/LocationHistoryMap';
import LocationHistoryTable from '@/components/bikes/LocationHistoryTable';
import EnhancedDateRangeSelector from '@/components/bikes/EnhancedDateRangeSelector';
import { BikeSearchSelect } from '@/components/search/BikeSearchSelect';
import { useBikeLocationHistory } from '@/hooks/useBikeLocationHistory';
import { supabase } from '@/integrations/supabase/client';
import { MapPin, Calendar, Bike } from 'lucide-react';
import { DateRange } from 'react-day-picker';

interface BikeData {
  id: string;
  model: string;
  serial_number?: string;
  gps_tag?: string;
  assigned_courier?: {
    name: string;
    id: string;
  };
  status?: string;
  battery_percentage?: number;
  rental_number?: string;
}

const LocationHistoryPage: React.FC = () => {
  const [selectedBikeId, setSelectedBikeId] = useState<string>('');
  const [availableBikes, setAvailableBikes] = useState<BikeData[]>([]);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 дней назад
    to: new Date()
  });

  const {
    data: locationHistory,
    loading,
    error,
    refresh
  } = useBikeLocationHistory(
    selectedBikeId, 
    undefined, 
    dateRange?.from || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), 
    dateRange?.to || new Date()
  );

  // Загружаем список доступных велосипедов с расширенной информацией
  useEffect(() => {
    const fetchBikes = async () => {
      try {
        const { data: bikesData, error: bikesError } = await supabase
          .from('bikes')
          .select(`
            id, 
            model, 
            serial_number, 
            gps_tag, 
            status,
            battery_percentage,
            assigned_to,
            couriers:assigned_to (
              id,
              name
            )
          `)
          .order('model');

        if (bikesError) {
          console.error('Ошибка при загрузке велосипедов:', bikesError);
          return;
        }

        // Отдельно получаем информацию об арендах для каждого велосипеда
        const { data: rentalsData, error: rentalsError } = await supabase
          .from('rentals')
          .select('bike_id, human_number, status')
          .in('status', ['active', 'pending']);

        if (rentalsError) {
          console.error('Ошибка при загрузке аренд:', rentalsError);
        }

        // Функция для безопасного извлечения строкового значения
        const extractSafeString = (value: any): string | undefined => {
          if (!value) return undefined;
          
          if (typeof value === 'string') {
            const trimmed = value.trim();
            return trimmed && trimmed !== '' ? trimmed : undefined;
          }
          
          if (typeof value === 'number') {
            return String(value);
          }
          
          // Все остальные типы (объекты, массивы и т.д.) игнорируем
          console.warn('Неподдерживаемый тип для human_number:', typeof value, value);
          return undefined;
        };

        const bikes: BikeData[] = (bikesData || []).map(bike => {
          // Находим активную или pending аренду для этого велосипеда
          const activeRental = rentalsData?.find(
            rental => rental.bike_id === bike.id && 
                     (rental.status === 'active' || rental.status === 'pending')
          );
          
          // Безопасно извлекаем human_number
          const safeRentalNumber = activeRental ? extractSafeString(activeRental.human_number) : undefined;
          
          return {
            id: bike.id,
            model: bike.model || 'Модель не указана',
            serial_number: bike.serial_number,
            gps_tag: bike.gps_tag,
            status: bike.status,
            battery_percentage: bike.battery_percentage,
            assigned_courier: bike.couriers && Array.isArray(bike.couriers) && bike.couriers.length > 0 ? {
              id: bike.couriers[0].id,
              name: bike.couriers[0].name
            } : undefined,
            rental_number: safeRentalNumber
          };
        });

        console.log('Загружено велосипедов:', bikes.length);
        // Показываем только те велосипеды, у которых есть rental_number для отладки
        const bikesWithRentals = bikes.filter(b => b.rental_number);
        if (bikesWithRentals.length > 0) {
          console.log('Велосипеды с активными арендами:', bikesWithRentals.map(b => ({
            id: b.id,
            model: b.model,
            rental_number: b.rental_number
          })));
        }
        
        setAvailableBikes(bikes);
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
      }
    };

    fetchBikes();
  }, []);

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
  };

  const handleRefresh = () => {
    refresh();
  };

  const handleBikeSelect = (bikeId: string, bike?: BikeData) => {
    setSelectedBikeId(bikeId);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <MapPin className="h-6 w-6" />
          История местоположений
        </h1>
        <Button onClick={handleRefresh} variant="outline">
          Обновить
        </Button>
      </div>

      {/* Фильтры */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bike className="h-5 w-5" />
            Фильтры
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Выберите велосипед</label>
              <BikeSearchSelect
                bikes={availableBikes}
                value={selectedBikeId}
                onValueChange={handleBikeSelect}
                placeholder="Поиск по модели, GPS тегу, курьеру, номеру аренды..."
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Период
              </label>
              <EnhancedDateRangeSelector
                dateRange={dateRange}
                onDateRangeChange={handleDateRangeChange}
                onRefresh={handleRefresh}
                isLoading={loading}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Результаты */}
      {selectedBikeId && (
        <div className="space-y-6">
          {/* Карта */}
          <Card>
            <CardHeader>
              <CardTitle>Карта маршрута</CardTitle>
            </CardHeader>
            <CardContent>
              <LocationHistoryMap
                bikeId={selectedBikeId}
                isAdmin={true}
              />
            </CardContent>
          </Card>

          {/* Таблица */}
          <Card>
            <CardHeader>
              <CardTitle>История перемещений</CardTitle>
            </CardHeader>
            <CardContent>
              <LocationHistoryTable
                locationHistory={locationHistory || []}
                showMileage={true}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {!selectedBikeId && (
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <div className="text-center text-muted-foreground">
              <Bike className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Выберите велосипед, чтобы просмотреть историю местоположений</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LocationHistoryPage;
